import React from "react";
import {PlusCircleFilled} from "@ant-design/icons";
import Dragger from "antd/es/upload/Dragger";
import {getTranslate, Translate, TranslateFunction} from "react-localize-redux";
import {connect} from "react-redux";
import {UploadChangeParam, UploadFile} from "antd/es/upload/interface";
import {message, Upload} from "antd";
import classNames from "classnames";
import {
    DisplayType,
    EditorType,
    ElementsValues,
    ElementsValuesType,
    getElementsValues,
    Nullable,
    RefElementsMap,
    UploadsConfig,
} from "@data-force/app";
import {FileTypeResult} from "file-type/browser";
import {ArgsProps} from "antd/es/message";
import {
    batchUploadsAreValid,
    calculateNumberOfTasks,
    determineBucketKey,
    formatString,
    getHistoryParams,
    getUploadFileList,
    initUploadsData,
    isPhaseExpired,
    mergeUploadBuckets,
    removeFileFromBucket,
} from "../../utils/common";
import StoreState from "../../interfaces/store/storeState";
import {RemoveSelectedFileAction, SetSelectedFilesAction} from "../../interfaces/store/actions/uploadFiles";
import {removeSelectedFile, setSelectedFiles} from "../../store/actions/uploadFiles";
import {startTaskWizard} from "../../store/actions/task";
import "./FilePicker.scss";
import HistoryParams from "../../interfaces/historyParams";
import {Result} from "../../types/types";
import {FILE_VALIDATION_CHUNK_SIZE} from "../../utils/constants";
import {setJobMaskData} from "../../store/actions/job";
import {JobMaskDataPayload} from "../../interfaces/store/actions/payload/job";
import {SetJobMaskDataAction} from "../../interfaces/store/actions/job";
import {
    FileTypeAndExtension,
    FileValidationProcess,
    FileValidationProcessStatus,
    UploadsData,
} from "../../interfaces/store/actions/payload/fileValidation";
import {TaskFile} from "../../interfaces/store/actions/payload/uploadFiles";
import {Task, TaskEntity} from "../../interfaces/store/actions/payload/task";
import {StartTaskWizardAction} from "../../interfaces/store/actions/task";
import {
    beforeUploadHandler,
    checkIfImageUrl,
    createBucketLimitAlert,
    createUploadFilesLimitAlert,
    fileIsValid,
    generatePreviewFile,
    getAvailableHolderIndex,
    handleTypeAndMediaInfo,
    mapFileToTask,
    renderFallbackIcon,
    renderThumbnail,
    createDeleteIcon,
} from "./filePickerUtils";
import {FileValidity, Thumbnails} from "../../interfaces/components/FilePickerTypes";
import {UploadsBucket, SelectedFiles, BucketCount} from "../../interfaces/store/reducers/uploadFilesState";
import {AddAlertsAction, ClearAlertsAction} from "../../interfaces/store/actions/layout";
import {addAlerts, clearAlerts} from "../../store/actions/layout";
import {FileSelectionType} from "../../interfaces/store/actions/payload/layout";

interface StateProps {
    translate: TranslateFunction
    fileSizeLimit: number
    batchUploads: UploadsConfig[]
    collectionRefElements: RefElementsMap
    elementsValues: ElementsValues
    activeTaskFile: Nullable<UploadFile>
    activeTask: Nullable<Task>
    tasks: TaskEntity[]
    mappedTaskIds: string[]
    mappedFiles: UploadFile[]
    numberOfRequiredFiles: number
    fileSelectionType: Nullable<FileSelectionType>
    uploadsData: UploadsData
    validBatchUploadsConfig: boolean
    finishedTaskIds: string[]
    displayType: DisplayType
    bucketCount: BucketCount
    isReadOnly: boolean
}

interface DispatchProps {
    setSelectedFiles(payload: SelectedFiles): SetSelectedFilesAction

    removeSelectedFile(payload: UploadFile): RemoveSelectedFileAction

    startTaskWizard(): StartTaskWizardAction

    setJobMaskData(payload: JobMaskDataPayload): SetJobMaskDataAction

    clearAlerts(): ClearAlertsAction

    addAlerts(payload: ArgsProps[]): AddAlertsAction
}

type Props = DispatchProps & StateProps;

class FilePicker extends React.Component<Props> {
    private static readonly translatePath: string = "components.FilePicker.FilePicker";

    /**
     * Collection of all valid task - files per bucket name, in single FilePicker session
     */
    private static bucketFilesMap: SelectedFiles = {};

    /**
     * Collection of all file validation processes.
     */
    private static validationProcesses: FileValidationProcess[] = [];

    constructor(props: Props) {
        super(props);

        this.onChangeHandler = this.onChangeHandler.bind(this);
        this.updateFileList = this.updateFileList.bind(this);
        this.initializeFileProcessing = this.initializeFileProcessing.bind(this);
        this.processFile = this.processFile.bind(this);
        this.completeValidationProcess = this.completeValidationProcess.bind(this);
        this.removeFile = this.removeFile.bind(this);
        this.renderDeleteIcon = this.renderDeleteIcon.bind(this);
        this.clearAlertMessages = this.clearAlertMessages.bind(this);
    }

    shouldComponentUpdate(nextProps: Readonly<Props>): boolean {
        const {
            activeTaskFile,
            activeTask,
            tasks,
            numberOfRequiredFiles,
            fileSelectionType,
            validBatchUploadsConfig,
            finishedTaskIds,
            batchUploads,
            displayType,
            mappedFiles,
            mappedTaskIds,
        }: Props = this.props;
        return (
            activeTaskFile !== nextProps.activeTaskFile
            || activeTask !== nextProps.activeTask
            || tasks !== nextProps.tasks
            || numberOfRequiredFiles !== nextProps.numberOfRequiredFiles
            || batchUploads !== nextProps.batchUploads
            || fileSelectionType !== nextProps.fileSelectionType
            || displayType !== nextProps.displayType
            || validBatchUploadsConfig !== nextProps.validBatchUploadsConfig
            || finishedTaskIds !== nextProps.finishedTaskIds
            || mappedFiles.length !== nextProps.mappedFiles.length
            || mappedTaskIds.length !== nextProps.mappedTaskIds.length
        );
    }

    componentDidUpdate(): void {
        const {
            numberOfRequiredFiles,
            mappedTaskIds,
            fileSelectionType,
            displayType,
        }: Props = this.props;

        if (displayType === DisplayType.INTAKER_WIZARD
            && fileSelectionType === FileSelectionType.BULK
            && numberOfRequiredFiles > 0
            && numberOfRequiredFiles === mappedTaskIds.length) {
            this.props.startTaskWizard();
        }
    }

    onChangeHandler({fileList, file}: UploadChangeParam): void {
        this.clearAlertMessages();
        this.props.setJobMaskData({visible: true, messageId: "validatingFiles", displaySpinner: true});
        if (file.status === "removed") {
            this.removeFile(file);
        } else {
            const currentFile: UploadFile = fileList.find(
                (current: UploadFile): boolean => current.uid === file.uid,
            ) as UploadFile;
            this.initializeFileProcessing(currentFile);
        }
    }

    /**
     * Actual removal of current file from list of valid files, hiding of mask
     * @param {UploadFile} file
     */
    removeFile(file: UploadFile): void {
        this.clearAlertMessages();
        FilePicker.bucketFilesMap = removeFileFromBucket(file, FilePicker.bucketFilesMap);
        this.props.removeSelectedFile(file);
        this.props.setJobMaskData({visible: false, messageId: "validatingFiles", displaySpinner: true});
    }

    /**
     * Clear alerts from both, screen and redux store.
     *
     * @return {void}
     */
    clearAlertMessages() {
        message.destroy();
        this.props.clearAlerts();
    }

    /**
     * Triggers file validation process.
     *
     * @param file {UploadFile}
     * @param processIndex {number}
     */
    processFile(file: UploadFile, processIndex: number): void {
        const {uid}: UploadFile = file;
        if (processIndex > -1) {
            const existingProcess: FileValidationProcess = FilePicker.validationProcesses[processIndex];
            if (existingProcess.status === FileValidationProcessStatus.READY) {
                existingProcess.status = FileValidationProcessStatus.IN_PROGRESS;
                handleTypeAndMediaInfo(file).then((values) => this.updateFileList(...values));
            }
        } else {
            FilePicker.validationProcesses.push({
                uid,
                status: FileValidationProcessStatus.IN_PROGRESS,
            });
            handleTypeAndMediaInfo(file).then((values) => this.updateFileList(...values));
        }
    }

    /**
     * Triggers file validation process, if number of running validation processes is within the limit.
     * Creates interval function otherwise.
     *
     * @param file {UploadFile}
     */
    initializeFileProcessing(file: UploadFile): void {
        const {uid}: UploadFile = file;
        const runningValidations: FileValidationProcess[] = FilePicker.validationProcesses.filter(
            (process: FileValidationProcess) => process.status === FileValidationProcessStatus.IN_PROGRESS,
        );
        const fileProcessIndex: number = FilePicker.validationProcesses.findIndex(
            (process: FileValidationProcess) => process.uid === uid,
        );
        if (runningValidations.length <= FILE_VALIDATION_CHUNK_SIZE) {
            this.processFile(file, fileProcessIndex);
        } else if (fileProcessIndex === -1) {
            const handle: number = window.setInterval(this.initializeFileProcessing.bind(this, file), 1000);
            FilePicker.validationProcesses.push({
                uid,
                status: FileValidationProcessStatus.READY,
                handle,
            });
        }
    }

    /**
     * Updates FilePicker.validationProcesses, when file validation is completed.
     *
     * @param {string} uid
     */
    completeValidationProcess(uid: string): void {
        const validationProcessIndex: number = FilePicker.validationProcesses.findIndex(
            (process: FileValidationProcess) => process.uid === uid,
        );
        if (validationProcessIndex > -1) {
            const validationProcess: FileValidationProcess = FilePicker.validationProcesses[validationProcessIndex];
            if (validationProcess.handle) {
                window.clearInterval(validationProcess.handle);
            }
            FilePicker.validationProcesses.splice(validationProcessIndex, 1, {
                uid,
                status: FileValidationProcessStatus.FINISHED,
            });
        }
    }

    /**
     *  Updates store on files upload.
     *
     *  @param {UploadFile} file
     *  @param sniffedType from {Nullable<FileTypeResult>}
     *  @param fileMediaInfo from {Nullable<Result>}
     *  @param {FileTypeAndExtension} fileTypeAndExtension
     */
    updateFileList(
        file: UploadFile,
        sniffedType: Nullable<FileTypeResult>,
        fileMediaInfo: Nullable<Result>,
        fileTypeAndExtension: FileTypeAndExtension,
    ): void {
        const {translatePath}: typeof FilePicker = FilePicker;
        const {
            numberOfRequiredFiles,
            fileSizeLimit,
            activeTask,
            fileSelectionType,
            tasks,
            mappedTaskIds,
            translate,
            collectionRefElements,
            elementsValues,
            uploadsData,
            validBatchUploadsConfig,
            finishedTaskIds,
            bucketCount,
        }: Props = this.props;
        const {fileType, fileExtension}: FileTypeAndExtension = fileTypeAndExtension;
        const maxFileSize: number = fileSizeLimit * 1024 * 1024;
        const {size, name, uid}: UploadFile = file;
        const {uploadsHolders}: UploadsData = uploadsData;

        const {isFileValid, validUploadsHolders, validationAlerts}: FileValidity = fileIsValid({
            fileMediaInfo,
            fileMimeType: fileType,
            fileName: name,
            fileExtension,
            uid,
            translate,
            collectionRefElements,
            elementsValues,
            uploadsData,
            translatePath,
        });
        if (validationAlerts.length) {
            this.props.addAlerts(validationAlerts);
        }

        this.completeValidationProcess(uid);
        const availableHolderIndex: number = getAvailableHolderIndex({
            uploadsHolders: validUploadsHolders,
            bucketFilesMap: FilePicker.bucketFilesMap,
            validBatchUploadsConfig,
            bucketCount,
        });
        const validFiles: UploadFile[] = getUploadFileList(FilePicker.bucketFilesMap);
        const fileSelectable: boolean = size !== undefined && size < maxFileSize
            && isFileValid
            && validFiles.length < numberOfRequiredFiles;

        if (fileSelectable && (availableHolderIndex > -1 || !uploadsHolders.length)) {
            const isDuplicate: boolean = validFiles.findIndex(
                (uploadFile: UploadFile): boolean => uploadFile.name === name,
            ) >= 0;
            if (!isDuplicate) {
                const taskFile: Nullable<TaskFile> = mapFileToTask({
                    uploadFile: file,
                    activeTask,
                    fileSelectionType,
                    mappedTaskIds,
                    tasks,
                    finishedTaskIds,
                });
                if (taskFile) {
                    const bucketKey: string = determineBucketKey({
                        useBucketConfig: !!uploadsHolders.length,
                        validBatchUploadsConfig,
                        uploadsHolder: validUploadsHolders[availableHolderIndex],
                    });
                    if (!FilePicker.bucketFilesMap[bucketKey]) {
                        FilePicker.bucketFilesMap[bucketKey] = {};
                    }
                    FilePicker.bucketFilesMap[bucketKey][taskFile.taskId] = file;
                    this.props.setSelectedFiles(FilePicker.bucketFilesMap);
                }
            } else {
                const duplicateTxt: string = this.props.translate(
                    `${FilePicker.translatePath}.duplicateFile.text`,
                ) as string;
                this.props.addAlerts([{
                    content: `${file.name} - ${duplicateTxt}`,
                    duration: 0,
                    type: "error",
                    key: `${file.name}-duplicate`,
                }]);
            }
        }

        if (validFiles.length === numberOfRequiredFiles) {
            const limitAlert: ArgsProps = createUploadFilesLimitAlert({
                validUploadsHolders: [],
                translatePath,
                translate,
            });
            this.props.addAlerts([limitAlert]);
        } else if (fileSelectable && uploadsHolders.length && availableHolderIndex < 0) {
            const bucketLimitAlert: ArgsProps = createBucketLimitAlert({
                validUploadsHolders,
                translatePath,
                translate,
            });
            this.props.addAlerts([bucketLimitAlert]);
        }

        const isValidationFinished: boolean = FilePicker.validationProcesses.findIndex(
            (process: FileValidationProcess) => process.status === FileValidationProcessStatus.IN_PROGRESS
                || process.status === FileValidationProcessStatus.READY,
        ) === -1;
        if (isValidationFinished) {
            this.props.setJobMaskData({visible: false, displaySpinner: false});
        }
    }

    /**
     * Creates "Delete" icon with appropriate aria-label attribute.
     *
     * @param {UploadFile} file
     * @return {React.ReactNode}
     */
    renderDeleteIcon(file: UploadFile): React.ReactNode {
        const {translatePath}: typeof FilePicker = FilePicker;
        const {translate}: Props = this.props;
        const {name}: UploadFile = file;

        return createDeleteIcon({
            translatePath,
            translate,
            fileName: name,
        });
    }

    render(): React.ReactElement {
        const {translatePath}: typeof FilePicker = FilePicker;
        const {
            numberOfRequiredFiles,
            translate,
            fileSizeLimit,
            uploadsData: {acceptedMimeTypes},
            fileSelectionType,
            mappedFiles,
            mappedTaskIds,
            isReadOnly,
        }: Props = this.props;
        const clickOrDragMessage: React.ReactNode = <Translate id={`${translatePath}.clickOrDragFiles.text`}/>;
        const pleaseSelectLoc: string = translate(`${translatePath}.pleaseSelect.text`) as string;
        const pleaseSelectMessage: string = formatString(
            pleaseSelectLoc, numberOfRequiredFiles.toString(), fileSizeLimit.toString(),
        );
        const reachOutToSupportMessage: string = translate(`${translatePath}.reachOutToSupport.text`) as string;
        const uploadVisible: boolean = !!mappedTaskIds.length;
        const draggerVisible: boolean = !mappedTaskIds.length;
        const fileList: UploadFile[] = mappedFiles.length <= numberOfRequiredFiles ? mappedFiles : [];
        const acceptAttribute: string = `${acceptedMimeTypes.join(",")}`;
        const filePickerClass: string = classNames({
            "df-file-picker": true,
            "df-single-file-picker": fileSelectionType === FileSelectionType.SINGLE,
            "df-multiple-file-picker": fileSelectionType === FileSelectionType.BULK,
        });

        return (
            <div className={filePickerClass}>
                {uploadVisible && (
                    <Upload
                        accept={acceptAttribute}
                        listType="picture-card"
                        multiple
                        beforeUpload={beforeUploadHandler}
                        previewFile={generatePreviewFile}
                        iconRender={renderFallbackIcon}
                        isImageUrl={checkIfImageUrl}
                        itemRender={renderThumbnail.bind(null, Thumbnails.VIDEO)}
                        onChange={this.onChangeHandler}
                        onRemove={this.removeFile}
                        fileList={fileList}
                        showUploadList={{
                            showPreviewIcon: false,
                            removeIcon: this.renderDeleteIcon,
                        }}
                        disabled={isReadOnly}
                    >
                        {fileList.length < numberOfRequiredFiles && (
                            <div className="df-upload-button">
                                <PlusCircleFilled/>
                                <div className="df-upload-button-text">
                                    <Translate id={`${translatePath}.addMoreFiles.text`}/>
                                </div>
                            </div>
                        )}
                    </Upload>
                )}
                {draggerVisible && (
                    <Dragger
                        multiple
                        className="df-upload-field"
                        beforeUpload={beforeUploadHandler}
                        onChange={this.onChangeHandler}
                        onRemove={this.removeFile}
                        showUploadList={false}
                        disabled={isReadOnly}
                    >

                        <div className="df-upload-field-content">
                            <div className="df-upload-drag"/>
                            <div className="df-click-drag-info">
                                {clickOrDragMessage}
                            </div>
                            <div className="df-please-select">
                                {pleaseSelectMessage}
                                <br/>
                                {reachOutToSupportMessage}
                            </div>
                        </div>
                    </Dragger>
                )}
            </div>
        );
    }
}

function mapStateToProps({
    localize,
    schema: {
        elementsValuesMap,
        parsedSchemas,
    },
    project,
    uploadFiles: {
        selectedFiles,
        finishedUploads: {
            finishedTaskIds,
            bucketCount,
        },
    },
    layout: {
        content: {
            fileSelectionType,
        },
    },
    job: {
        activeTask, tasks, jobPhase,
    },
}: StoreState): StateProps {
    const historyParams: Nullable<HistoryParams> = getHistoryParams();
    const schemaKey: string = `${project.projectDetails?.schemaId}_${historyParams?.phase}`;
    const editor: Nullable<EditorType> = parsedSchemas[schemaKey] as EditorType;
    const taskId: string = activeTask?.id || "";
    const numberOfRequiredFiles: number = calculateNumberOfTasks(jobPhase?.phaseStats);
    const uploadBucket: UploadsBucket = mergeUploadBuckets(selectedFiles);
    const collectionRefElements: RefElementsMap = editor?.refElements || {};
    const elementsValues: ElementsValues = getElementsValues({
        metadataId: historyParams?.jobId,
        elementsValuesType: ElementsValuesType.JOB,
        schemaKey,
        elementsValuesMap,
    });
    const translate: TranslateFunction = getTranslate(localize);
    const validBatchUploadsConfig: boolean = editor?.batchUploads && editor?.uploadReference
        ? batchUploadsAreValid(editor.batchUploads, editor.uploadReference)
        : false;

    return {
        translate,
        fileSizeLimit: editor?.fileSizeLimitMegaBytes || 0,
        collectionRefElements,
        elementsValues,
        activeTaskFile: uploadBucket[taskId],
        activeTask,
        tasks,
        mappedTaskIds: Object.keys(uploadBucket),
        mappedFiles: Object.values(uploadBucket),
        numberOfRequiredFiles,
        fileSelectionType,
        batchUploads: editor?.batchUploads || [],
        uploadsData: initUploadsData({
            editor,
            collectionRefElements,
            elementsValues,
            translate,
            translationPath: "components.FilePicker.FilePicker",
            tasks,
            elementsValuesMap,
            schemaKey,
            uploadBucket,
        }),
        validBatchUploadsConfig,
        finishedTaskIds,
        displayType: editor?.layout || DisplayType.INTAKER_WIZARD,
        bucketCount,
        isReadOnly: isPhaseExpired(jobPhase.accessStatus),
    };
}

const mapDispatchToProps: DispatchProps = {
    setJobMaskData,
    setSelectedFiles,
    removeSelectedFile,
    startTaskWizard,
    clearAlerts,
    addAlerts,
};

export default connect(mapStateToProps, mapDispatchToProps)(FilePicker);
